import { z } from 'zod';

import { CountryCodeSchema } from '../../questions/country-code/country-code';
import { UserSchema } from '../user';

/**
 * The contact-verification steps, in the order the member walks them (P30):
 * the address first, the number after it. A wali verifies too — the root's
 * `contact` stage comes before `guardian` (decision 15).
 */
export const ONBOARDING_STEPS = ['verify_email', 'verify_phone'] as const;

export const OnboardingStepSchema = z.enum(ONBOARDING_STEPS);

export type OnboardingStep = z.infer<typeof OnboardingStepSchema>;

/**
 * What the member still owes before the answers may move them past
 * `registered` — computed by the server from the two timestamps on the row and
 * the `registration.*` settings, **only while `status === 'registered'`**:
 * every other status answers `next: null`, so an account that predates the
 * feature is never pulled back to a step it was never asked for (decision 2).
 *
 * The phone facts are the policy the picker draws from (P30b2):
 * `countries: null` means any country; exactly one fixes the picker to it.
 */
export const OnboardingStateSchema = z.object({
  next: OnboardingStepSchema.nullable(),
  email: z.object({ required: z.boolean() }),
  phone: z.object({
    required: z.boolean(),
    countries: z.array(CountryCodeSchema).nullable(),
    defaultCountry: CountryCodeSchema.nullable(),
  }),
});

export type OnboardingState = z.infer<typeof OnboardingStateSchema>;

/**
 * The member as `GET /me` and `PATCH /me/visibility` answer them — the `User`
 * every client already caches, plus the onboarding block (decision 18).
 *
 * The block lives here and **not** on `UserSchema`: `User` is embedded in the
 * sign-in session, the staff list and every lookup, and a settings-composed
 * block there would be computed by five producers and leak pending-code state
 * to staff. Only the two routes that feed the mobile `me` cache carry it.
 */
export const MeSchema = UserSchema.extend({
  onboarding: OnboardingStateSchema,
});

export type Me = z.infer<typeof MeSchema>;
